import { type TypedDocumentNode, gql } from '@apollo/client';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { ANY_SPORT_SCOUTING_CENTER_WATCHLISTS } from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary } from '@sorare/core/src/lib/glossary';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import {
  getSubscribableWatchlistTypes,
  useHasFollowedPlayers,
} from 'hooks/useHasFollowedPlayers';

import { WatchlistEmptyState } from '../WatchlistEmptyState';
import { FavoritesHeader } from './FavoritesHeader';
import type {
  FavoritesWatchlistPageQuery,
  FavoritesWatchlistPageQueryVariables,
} from './__generated__/index.graphql';

const FAVORITES_WATCHLIST_PAGE_QUERY = gql`
  query FavoritesWatchlistPageQuery(
    $sport: Sport!
    $types: [SubscribableType!]
  ) {
    so5 {
      so5Fixture(type: UPCOMING, eventType: CLASSIC, sport: $sport) {
        slug
      }
    }
    currentUser {
      slug
      ...useHasFollowedPlayers_currentUser
    }
  }
  ${useHasFollowedPlayers.fragments.currentUser}
` as TypedDocumentNode<
  FavoritesWatchlistPageQuery,
  FavoritesWatchlistPageQueryVariables
>;

type SearchComponentProps = {
  so5FixtureSlug?: string;
};

type Props = {
  sport: Sport;
  SearchComponent: React.ComponentType<SearchComponentProps>;
};

export const FavoritesPage = ({ sport, SearchComponent }: Props) => {
  const { formatMessage } = useIntlContext();
  const { data, loading } = useQuery(FAVORITES_WATCHLIST_PAGE_QUERY, {
    variables: {
      sport,
      types: getSubscribableWatchlistTypes(sport),
    },
    nextFetchPolicy: 'network-only',
  });
  const { hasFollowedPlayers, followedPlayers, count } = useHasFollowedPlayers({
    currentUser: data?.currentUser,
  });

  const so5FixtureSlug = data?.so5?.so5Fixture?.slug;

  return (
    <>
      <PageHeader
        text={formatMessage(glossary.favorites)}
        backTo={generateSportPath(ANY_SPORT_SCOUTING_CENTER_WATCHLISTS, {
          sport,
        })}
        disableBackBrowser
      />
      <FavoritesHeader
        playerUrls={
          followedPlayers
            ?.map(player => 'fullPictureUrl' in player && player.fullPictureUrl)
            .filter(Boolean) || []
        }
        count={count}
      />
      {!!loading && <LoadingIndicator fullScreen />}
      {hasFollowedPlayers && !loading ? (
        <SearchComponent so5FixtureSlug={so5FixtureSlug} />
      ) : (
        <WatchlistEmptyState />
      )}
    </>
  );
};
